import { RevealEntry } from '@/@studio-ai/types';
import {
    cloneElement,
    isValidElement,
    ReactElement,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { TextDisplay } from '../components/text-display';
import { CHUNKED_REVEAL_CONSTANTS } from '../constants';
import type { UseChunkedRevealOptions, UseChunkedRevealResult } from '../props';

function buildRevealEntries(elements: ReactElement[]): RevealEntry[] {
    const entries: RevealEntry[] = [];

    for (const element of elements) {
        if (isValidElement(element) && element.type === TextDisplay) {
            const text = (element.props as { text?: string }).text ?? '';
            const paragraphs = text.split(/\n{2,}/).filter((p: string) => p.trim().length > 0);

            entries.push({
                element,
                kind: 'text',
                paragraphs: paragraphs.length > 0 ? paragraphs : [text],
            });
        } else {
            entries.push({ element, kind: 'other' });
        }
    }

    return entries;
}

function countTotalSteps(entries: RevealEntry[]): number {
    let total = 0;
    for (const entry of entries) {
        total += entry.kind === 'text' && entry.paragraphs ? entry.paragraphs.length : 1;
    }
    return total;
}

function renderAtStep(entries: RevealEntry[], step: number): ReactElement[] {
    const result: ReactElement[] = [];
    let remaining = step;

    for (const entry of entries) {
        if (remaining <= 0) break;

        if (entry.kind === 'other') {
            result.push(entry.element);
            remaining -= 1;
        } else {
            const paragraphs = entry.paragraphs!;
            const visibleCount = Math.min(remaining, paragraphs.length);
            const visibleText = paragraphs.slice(0, visibleCount).join('\n\n');

            result.push(
                cloneElement(entry.element, {
                    key: entry.element.key,
                    text: visibleText,
                } as Record<string, unknown>),
            );
            remaining -= visibleCount;
        }
    }

    return result;
}

/**
 * Progressively reveals an array of React elements in staggered chunks.
 *
 * - TextDisplay elements grow paragraph-by-paragraph within a single component,
 *   preserving natural spacing.
 * - All other elements (charts, tables, etc.) are revealed as whole units.
 * - Only activates when the `animate` flag is true (set by the streaming layer
 *   on live responses). History-loaded messages never have this flag.
 */
export function useChunkedReveal(
    elements: ReactElement[],
    options: UseChunkedRevealOptions,
): UseChunkedRevealResult {
    const {
        enabled,
        isLatest,
        messageId,
        animate,
        delayMs = CHUNKED_REVEAL_CONSTANTS.DEFAULT_DELAY_MS,
        onRevealComplete,
    } = options;

    const shouldChunk = enabled && isLatest && animate;

    const entries = useMemo(
        () => (shouldChunk ? buildRevealEntries(elements) : []),
        [elements, shouldChunk],
    );

    const totalSteps = useMemo(
        () => (shouldChunk ? countTotalSteps(entries) : 0),
        [entries, shouldChunk],
    );

    const hasRevealedRef = useRef<Set<string>>(new Set());
    const shouldAnimate = shouldChunk && !hasRevealedRef.current.has(messageId);

    const [step, setStep] = useState(() => {
        if (!shouldAnimate) return totalSteps;
        return 1;
    });

    const prevMessageIdRef = useRef(messageId);
    if (prevMessageIdRef.current !== messageId) {
        prevMessageIdRef.current = messageId;
        if (shouldAnimate) {
            setStep(1);
        }
    }

    const onRevealCompleteRef = useRef(onRevealComplete);
    onRevealCompleteRef.current = onRevealComplete;

    useEffect(() => {
        if (!shouldAnimate) {
            setStep(totalSteps);
            return;
        }

        if (step >= totalSteps) {
            hasRevealedRef.current.add(messageId);
            onRevealCompleteRef.current?.();
            return;
        }

        const timer = setTimeout(() => {
            setStep((prev) => Math.min(prev + 1, totalSteps));
        }, delayMs);

        return () => clearTimeout(timer);
    }, [shouldAnimate, step, totalSteps, delayMs, messageId]);

    if (!shouldChunk) {
        return { content: elements, isRevealing: false };
    }

    const content = renderAtStep(entries, step);
    const isRevealing = step < totalSteps;

    return { content, isRevealing };
}
